<template>
  <div
    :class="{
      'display-metric': true,
      '-loading': loading,
      '-small': size === 'small',
      '-large': size === 'large',
    }"
  >
    <div class="_content">
      <div
        :class="['_value', { '-clickable': clickable }]"
        :style="valueStyle"
        :role="clickable ? 'button' : null"
        :tabindex="clickable ? 0 : null"
        @click="onActivate"
        @keydown.enter.prevent="onActivate"
        @keydown.space.prevent="onActivate"
      >
        {{ value }}
      </div>
      <div
        v-if="titleEnabled"
        class="_caption"
      >
        {{ title || unit }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { optionColor } from 'shared/constants.json';
import { optionTextColorMap } from 'shared/lib/color-map';

const props = defineProps({
  value: {
    type: [Number, Object],
    default: () => null,
  },
  unit: {
    type: String,
    default: '',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'medium',
    validator: (value) => ['small', 'medium', 'large'].includes(value),
  },
  color: {
    type: String,
    default: null,
  },
  title: {
    type: String,
    default: '',
  },
  titleEnabled: {
    type: Boolean,
    default: false,
  },
  clickable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['activate']);

const onActivate = () => {
  if (props.clickable) emit('activate');
};

const valueStyle = computed(() => {
  if (!props.color || props.color === optionColor.default) {
    return {};
  }
  const c = optionTextColorMap[props.color];
  if (!c) {
    return {};
  }
  return { color: c };
});

</script>

<style scoped lang="scss">
.display-metric {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100%;

  ._content {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-top: -1rem;
  }

  ._value {
    font-size: 72px;
    font-weight: $font-weight-bold;
    line-height: 1;
    color: $font-color-primary;

    &.-clickable {
      cursor: pointer;

      &:hover {
        background-color: $hover-color;
        border-radius: $btn-border-radius;
      }

      &:focus-visible {
        outline: 2px solid $primary-color;
        outline-offset: 2px;
        border-radius: 4px;
      }
    }
  }

  ._caption {
    font-size: $font-size-2;
    line-height: 1.2;
    margin-top: 1.2rem;
    color: $font-color-secondary;
    text-align: center;
    max-width: 100%;
  }

  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.2; }
    100% { opacity: 1; }
  }

  &.-loading {
    ._value {
      animation: pulse 1s infinite;
    }
  }

  &.-small {
    ._value {
      font-size: 48px;
    }

    ._caption {
      font-size: $font-size-4;
    }
  }

  &.-large {
    ._value {
      font-size: 96px;
    }

    ._caption {
      font-size: $font-size-8;
    }
  }
}
</style>
